import React, { useCallback, useEffect, useRef } from 'react';

import debounce from '../../../utils/debounce';
import { type Key } from '../Table.types';

import MeasureCell from './MeasureCell';

export interface MeasureCellProps {
    onColumnsResize?: (value: Map<Key, number>) => void;
    columnsKey: Key[];
    onRowResize?: (width: number) => void;
}

const MeasureRow = ({ columnsKey, onColumnsResize, onRowResize }: MeasureCellProps) => {
    const rowRef = useRef<HTMLTableRowElement>(null);
    const columnsWidthRef = useRef<Map<Key, number>>(new Map());

    const resizeHandler = useCallback(() => {
        if (onColumnsResize) {
            const newColumnsWidthRef = new Map(columnsWidthRef.current);
            columnsKey.forEach((columnKey) => {
                const column = newColumnsWidthRef.get(columnKey);
                if (column) {
                    newColumnsWidthRef.set(columnKey, column);
                }
            });
            onColumnsResize(newColumnsWidthRef);
        }
        onRowResize?.(rowRef.current?.offsetWidth ?? 0);
    }, []);

    const debounceResizeHandler = debounce(resizeHandler, 200);

    useEffect(() => {
        resizeHandler();
    }, []);

    useEffect(() => {
        const listObserver = new ResizeObserver(debounceResizeHandler);

        if (rowRef.current) {
            listObserver.observe(rowRef.current);
        }

        return () => {
            if (rowRef.current) {
                listObserver.unobserve(rowRef.current);
            }
        };
    }, [rowRef?.current]);

    const onColumnResize = (columnKey: Key, width: number) => {
        const valueChanged = columnsWidthRef.current.get(columnKey) !== width;
        if (valueChanged) {
            columnsWidthRef.current.set(columnKey, width);
            debounceResizeHandler();
        }
    };

    return (
        <tr aria-hidden="true" style={{ height: 0, fontSize: 0 }} ref={rowRef}>
            {columnsKey.map((columnKey) => (
                <MeasureCell
                    key={columnKey}
                    columnKey={columnKey}
                    onColumnResize={onColumnResize}
                />
            ))}
        </tr>
    );
};

export default MeasureRow;
